import { defineStore } from 'pinia'

import {
  type PageNumberPaginationParameters,
  createSecret,
  destroySecret,
  listSecrets,
  retrieveSecret,
  updateSecret,
} from '@/api'
import type { PageNumberPagination } from '@/types'
import type { Secret, SecretLight } from '@/types/secret'

interface State {
  secretPage: PageNumberPagination<SecretLight> | null
  secrets: { [name: string]: Secret }
}

export const useSecretStore = defineStore('secret', {
  state: (): State => ({
    secretPage: null,
    secrets: {},
  }),
  actions: {
    async list(params: PageNumberPaginationParameters = {}) {
      this.secretPage = await listSecrets(params)
    },

    async get(name: string) {
      this.secrets[name] = await retrieveSecret(name)
    },

    async create({ name, content }: Pick<Secret, 'name' | 'content'>) {
      const secretLight = await createSecret({ name, content })
      this.secrets[name] = {
        ...secretLight,
        // Only the SecretLight is returned from the API but we can deduce the other attributes and prefill the store
        content,
        rights: ['read', 'write', 'admin'],
      }
    },

    async update(name: string, payload: Partial<Pick<Secret, 'name' | 'content'>>) {
      const resp = await updateSecret(name, payload)
      // Remove the previous secret from the store as they are indexed by name
      if (payload.name && payload.name != name) {
        delete this.secrets[name]
      }
      this.secrets[resp.name] = resp
    },

    async delete(name: string) {
      await destroySecret(name)
      delete this.secrets[name]
    },
  },
})
